import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../../atoms/Button/Button';
import { Text } from '../../../../atoms/Text/Text';
import { ProgressBar } from '../../../../molecules/ProgressBar/ProgressBar';
import { Table, TableSize } from '../../../../molecules/Table/Table';
import { InlineText } from '../../../../molecules/inputs/inline/InlineText/InlineText';
import { InlineSelect } from '../../../../molecules/inputs/inline/InlineSelect/InlineSelect';
import { InlineFlagsSelect } from '../../../../molecules/inputs/inline/InlineFlagsSelect/InlineFlagsSelect';
import { isEditable, isRetryable } from '../../utils';
/** Resolves the Name cell's secondary meta line -- see `types.ts`'s label doc comments for the
 *  five possible strings this can render. */
function metaFor(item, isUploading, labels) {
    if (item.status === 'done')
        return { text: labels.metaUploaded(item.sizeLabel), isError: false };
    if (item.status === 'finalizing')
        return { text: labels.metaFinalizing(item.sizeLabel), isError: false };
    if (isRetryable(item.status))
        return { text: labels.metaFailed(item.sizeLabel), isError: true };
    if (item.status === 'pending' && isUploading)
        return { text: labels.metaWaiting(item.sizeLabel), isError: false };
    // 'pending' while the batch isn't running, and 'uploading' itself (the ProgressBar below
    // carries the live percentage) both show the plain size line.
    return { text: labels.metaSize(item.sizeLabel), isError: false };
}
/**
 * Puts the caret at the end of a contenteditable without selecting its text. IP-16588 asks for the
 * cursor to be *placed* so the field reads as editable -- a select-all would mean the next
 * keystroke wipes the filename the user just chose.
 */
function placeCaretAtEnd(element) {
    element.focus();
    const selection = window.getSelection();
    if (!selection)
        return;
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
}
function NameCell({ item, isUploading, labels, autoFocus, onAutoFocusApplied, onNameChange }) {
    const meta = metaFor(item, isUploading, labels);
    const cellRef = useRef(null);
    useEffect(() => {
        var _a;
        if (!autoFocus)
            return;
        // `InlineEditBase` renders a bare contenteditable with no role or ref of its own, so the
        // editable node is reached through the cell rather than handed over directly.
        const editable = (_a = cellRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[contenteditable="true"]');
        if (editable)
            placeCaretAtEnd(editable);
        onAutoFocusApplied();
    }, [autoFocus, onAutoFocusApplied]);
    const handleBlur = (event) => {
        var _a;
        const target = event.currentTarget;
        const raw = ((_a = target.textContent) !== null && _a !== void 0 ? _a : '').trim();
        const next = raw || item.originalName;
        if (!raw) {
            // E2E-12: emptying reverts to the ORIGINAL filename, not the current one -- deliberately
            // different from the main attachments table's Name cell (E2E-27), which reverts to the
            // current name instead.
            target.textContent = item.originalName;
        }
        // Blurring without having edited anything must not push an update. Since IP-16588 focuses this
        // field as soon as a file is staged, the very next thing the user does is usually click the
        // Category dropdown in the same row -- and an unconditional commit here re-rendered the row
        // between that click's mousedown and mouseup, so the dropdown opened and shut again and the
        // category could not be set at all.
        if (next === item.name)
            return;
        onNameChange(item.id, next);
    };
    return (React.createElement("div", { className: "qmb-ui-add-attachments-brushaway__name-cell", ref: cellRef },
        React.createElement("span", { className: "qmb-ui-add-attachments-brushaway__name-row" },
            React.createElement(InlineText, { width: "full", disableEdit: !isEditable(item.status), onBlur: handleBlur }, item.name),
            item.extension && (React.createElement(Text, { variant: "body", as: "span", className: "qmb-ui-add-attachments-brushaway__name-extension" },
                ".",
                item.extension))),
        React.createElement(Text, { variant: "x-small", as: "span", className: `qmb-ui-add-attachments-brushaway__meta${meta.isError ? ' qmb-ui-add-attachments-brushaway__meta--error' : ''}` }, meta.text),
        item.status === 'uploading' && (React.createElement(ProgressBar, { value: item.progress, className: "qmb-ui-add-attachments-brushaway__progress" }))));
}
function SelectCell({ item, column, disabled }) {
    var _a;
    return (React.createElement(InlineSelect, { options: column.options.map((option) => ({ value: option.value, label: option.label })), value: ((_a = item.fields[column.key]) !== null && _a !== void 0 ? _a : null), onChange: (value) => column.onChange(item.id, value), disabled: disabled, showSearch: true, footerAction: column.footerActionLabel && column.onRequestFooterAction
            ? {
                label: column.footerActionLabel,
                onSelect: () => { var _a; return (_a = column.onRequestFooterAction) === null || _a === void 0 ? void 0 : _a.call(column, (createdValue) => column.onChange(item.id, createdValue)); }
            }
            : undefined }));
}
function FlagsCell({ item, column, disabled }) {
    var _a;
    const values = ((_a = item.fields[column.key]) !== null && _a !== void 0 ? _a : []);
    return (React.createElement(InlineFlagsSelect, { options: column.options, values: values, summary: column.getSummary(values), onChange: (next) => column.onChange(item.id, next), disabled: disabled }));
}
/** Dispatches one consumer-supplied column descriptor to the cell that renders it. */
function QueueCell({ item, column }) {
    const disabled = !isEditable(item.status);
    switch (column.kind) {
        case 'select':
            return React.createElement(SelectCell, { item: item, column: column, disabled: disabled });
        case 'flags':
            return React.createElement(FlagsCell, { item: item, column: column, disabled: disabled });
        default:
            return React.createElement(React.Fragment, null, column.renderer({ item, disabled }));
    }
}
function ActionsCell({ item, labels, onRetryItem, onRemoveItem }) {
    const fileName = item.extension ? `${item.name}.${item.extension}` : item.name;
    return (React.createElement("span", { className: "qmb-ui-add-attachments-brushaway__row-actions" },
        isRetryable(item.status) && (React.createElement(Button, { "aria-label": labels.retryActionLabel, onClick: (event) => {
                event.stopPropagation();
                onRetryItem(item.id);
            } },
            React.createElement("i", { className: "fa-light fa-arrow-rotate-right", "aria-hidden": "true" }))),
        item.status !== 'uploading' && (React.createElement(Button, { "aria-label": labels.removeActionLabel(fileName), onClick: (event) => {
                event.stopPropagation();
                onRemoveItem(item.id);
            } },
            React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" })))));
}
/**
 * The staged-queue table rendered via `FileHandler`'s `renderList` render-prop. Deliberately NOT
 * `FileHandler`'s own built-in Preview/Name/Type/actions columns -- the design has no Preview
 * column and no Size column (folded into the Name cell's meta line instead), so the hardcoded
 * default table is the wrong shape for this surface (`docs/ai-knowledge/frontend/file-handler.md`).
 */
export function QueueTable({ items, isUploading, columns, labels, onNameChange, onRetryItem, onRemoveItem }) {
    // IP-16588: the first row of each newly staged batch takes the caret, so it is obvious the name
    // is editable. Tracked by item id rather than row index -- an index would re-fire every time a
    // row above it is removed, yanking the caret out of a field the user is typing in.
    //
    // `null` rather than an empty Set on the first pass is deliberate: `FileHandler` only renders
    // this table once there is something to list, so the opening batch arrives already mounted and
    // an empty Set would class it as "already seen" and skip the very case this exists for.
    const seenIdsRef = useRef(null);
    const [focusId, setFocusId] = useState(null);
    /**
     * Ids whose one-shot focus has already been spent.
     *
     * A REF, not state, and held in the parent rather than in `NameCell`:
     *   * state would re-render and drop the caret the effect has just placed (it did — two
     *     IP-16588 examples went red);
     *   * a ref inside `NameCell` would reset on remount, which is precisely when the bug fires.
     *
     * Without this, the focus was not one-shot at all: any remount of the row re-grabbed the caret.
     * Clicking from one inline queue field into another re-rendered the row via the first field's
     * commit, focus jumped back to Name, and the user's next keystrokes were appended to the
     * FILENAME instead of the field they had clicked into.
     */
    const focusAppliedRef = useRef(new Set());
    useEffect(() => {
        const seen = seenIdsRef.current;
        seenIdsRef.current = new Set(items.map((item) => item.id));
        const firstNew = items.find((item) => (seen === null || !seen.has(item.id)) && isEditable(item.status));
        if (firstNew)
            setFocusId(firstNew.id);
    }, [items]);
    const tableColumns = useMemo(() => [
        {
            dataKey: 'name',
            label: labels.columnName,
            columnType: 'primary',
            renderer: ({ item }) => (React.createElement(NameCell, { item: item, isUploading: isUploading, labels: labels, autoFocus: item.id === focusId && !focusAppliedRef.current.has(item.id), onAutoFocusApplied: () => {
                    focusAppliedRef.current.add(item.id);
                }, onNameChange: onNameChange }))
        },
        ...columns.map((column) => ({
            dataKey: column.key,
            label: column.label,
            width: column.width,
            renderer: ({ item }) => React.createElement(QueueCell, { item: item, column: column })
        })),
        {
            dataKey: 'typeLabel',
            label: labels.columnType,
            width: '72px'
        },
        {
            dataKey: 'actions',
            label: '',
            columnType: 'action',
            renderer: ({ item }) => (React.createElement(ActionsCell, { item: item, labels: labels, onRetryItem: onRetryItem, onRemoveItem: onRemoveItem }))
        }
    ], [labels, isUploading, columns, focusId, onNameChange, onRetryItem, onRemoveItem]);
    return (React.createElement(Table, { className: "qmb-ui-add-attachments-brushaway__queue-table", variant: "detail", size: TableSize.Full, selectable: false, columns: tableColumns, data: items, getItemId: (item) => item.id }));
}
